import { useState, useMemo, useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { Sparkles, X, Search } from 'lucide-react'
import { DotSphereSpinner } from '@/components/ui/DotSphereSpinner'
import { objectsApi } from '@/features/objects/api'
import type { RunbookTask } from '@/types/entities'
import { TranslatableText } from '@/components/ui/TranslatableText'

interface SelectedTechnology {
    id: number
    name: string
    category: string
}

interface AiGenerateRunbookDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    onGenerated: (
        description: string,
        tasks: RunbookTask[],
        meta?: { technologies: SelectedTechnology[]; generationContext: string }
    ) => void
    // Pre-fill the screen when regenerating an existing runbook
    initialTechnologies?: SelectedTechnology[]
    initialContext?: string
}

// Strip model name from status messages (e.g. "(using CLAUDE)" or "(using GPT-4)")
function cleanStatusMessage(msg: string): string {
    return msg.replace(/\s*\(using\s+[^)]+\)/gi, '')
}

export function AiGenerateRunbookDialog({ open, onOpenChange, onGenerated, initialTechnologies, initialContext }: AiGenerateRunbookDialogProps) {
    const { t } = useTranslation()
    const [selectedTechnologies, setSelectedTechnologies] = useState<SelectedTechnology[]>([])
    const [additionalContext, setAdditionalContext] = useState('')

    // Pre-fill from an existing runbook's saved generation inputs when the dialog opens
    useEffect(() => {
        if (open) {
            setSelectedTechnologies(initialTechnologies && initialTechnologies.length > 0 ? initialTechnologies : [])
            setAdditionalContext(initialContext || '')
        }
    }, [open, initialTechnologies, initialContext])
    const [generating, setGenerating] = useState(false)
    const [statusMessage, setStatusMessage] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [technologySearch, setTechnologySearch] = useState('')
    const [selectedScenario, setSelectedScenario] = useState<{ id: number; name: string; description: string } | null>(null)

    // Fetch recovery scenarios
    const { data: scenariosData, isLoading: scenariosLoading } = useQuery({
        queryKey: ['scenarios-all'],
        queryFn: async () => {
            const response = await objectsApi.getAllScenarios()
            return response.data?.data || response.data || []
        },
        enabled: open,
    })

    // Use the same API as ObjectDetailsTab - returns all technologies, no admin role needed
    const { data: technologiesData, isLoading: technologiesLoading } = useQuery({
        queryKey: ['technologies'],
        queryFn: async () => {
            const response = await objectsApi.getTechnologies()
            return response.data?.data || response.data || []
        },
        enabled: open,
    })

    const filteredTechnologies = useMemo(() => {
        if (!technologiesData || !Array.isArray(technologiesData)) return []
        if (!technologySearch) return technologiesData
        const search = technologySearch.toLowerCase()
        return technologiesData.filter((tech: any) => {
            const name = (tech.name || '').toLowerCase()
            const category = (tech.category || '').toLowerCase()
            return name.includes(search) || category.includes(search)
        })
    }, [technologiesData, technologySearch])

    // Build readable description
    const builtDescription = useMemo(() => {
        if (selectedTechnologies.length === 0) return ''
        const parts = selectedTechnologies.map((t) => t.name)
        const techDesc = parts.reduce((acc, part, i) => {
            if (i === 0) return part
            if (i === 1) return `${acc} on ${part}`
            return `${acc} with ${part}`
        }, '')
        return techDesc
    }, [selectedTechnologies])

    // Full description including scenario
    const fullDescription = useMemo(() => {
        if (!builtDescription) return ''
        if (selectedScenario) {
            return `${builtDescription} — ${selectedScenario.name}`
        }
        return builtDescription
    }, [builtDescription, selectedScenario])

    const handleAddTechnology = (techId: string) => {
        if (techId === 'clear' || !technologiesData) return
        const tech = technologiesData.find((t: any) => t.id === Number(techId))
        if (!tech || selectedTechnologies.some((s) => s.id === tech.id)) return
        setSelectedTechnologies((prev) => [
            ...prev,
            { id: tech.id, name: tech.name, category: tech.category },
        ])
        setTechnologySearch('')
    }

    const handleRemoveTechnology = (id: number) => {
        setSelectedTechnologies((prev) => prev.filter((t) => t.id !== id))
    }

    const handleGenerate = useCallback(async () => {
        if (selectedTechnologies.length === 0) return
        setGenerating(true)
        setError(null)
        setStatusMessage('Connecting to AI assistant...')

        try {
            const primaryTech = selectedTechnologies[0]
            // Pass the category from the technology table directly - let the backend
            // orchestrator's determineRunbookAgent() handle agent selection based on
            // both category and system name
            const targetCategory = primaryTech.category.toLowerCase().replace(/\s+/g, '-')

            // Build task description with scenario context
            const scenarioContext = selectedScenario
                ? `Recovery scenario: ${selectedScenario.name}${selectedScenario.description ? ` (${selectedScenario.description})` : ''}.`
                : ''
            const taskDescription = `Develop a detailed runbook to recover ${builtDescription}. ${scenarioContext}${additionalContext ? ` ${additionalContext}` : ''}`

            const response = await fetch('/api/ai/runbooks/generate', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    mode: 'master',
                    target: {
                        category: targetCategory,
                        system: primaryTech.name,
                        technologies: selectedTechnologies.map((t) => ({
                            category: t.category, name: t.name,
                        })),
                        scenario: selectedScenario ? {
                            name: selectedScenario.name,
                            description: selectedScenario.description,
                        } : undefined,
                    },
                    scenarios: [taskDescription],
                    format: 'json',
                }),
            })

            if (!response.ok && !response.headers.get('content-type')?.includes('text/event-stream')) {
                throw new Error(`AI server error: ${response.status}`)
            }

            const reader = response.body?.getReader()
            const decoder = new TextDecoder()
            let runbookContent: any[] = []
            let buffer = ''

            if (reader) {
                while (true) {
                    const { done, value } = await reader.read()
                    if (done) break
                    buffer += decoder.decode(value, { stream: true })
                    const lines = buffer.split('\n')
                    buffer = lines.pop() || ''
                    for (const line of lines) {
                        if (line.startsWith('event:')) continue
                        if (line.startsWith('data: ')) {
                            try {
                                const data = JSON.parse(line.slice(6))
                                if (data.status) setStatusMessage(data.status)
                                if (data.content) runbookContent = Array.isArray(data.content) ? data.content : []
                                if (data.error) throw new Error(data.error)
                            } catch { /* skip */ }
                        }
                    }
                }
            }

            if (runbookContent.length > 0) {
                // Robust field extraction - handle any case/naming variation
                const getField = (obj: any, ...keys: string[]) => {
                    for (const key of keys) {
                        if (obj[key] !== undefined && obj[key] !== null) return obj[key]
                        // Try case-insensitive match
                        const found = Object.keys(obj).find(k => k.toLowerCase() === key.toLowerCase())
                        if (found && obj[found] !== undefined && obj[found] !== null) return obj[found]
                    }
                    return undefined
                }
                const tasks: RunbookTask[] = runbookContent.map((task: any, index: number) => {
                    const deps = getField(task, 'Dependencies', 'dependencies', 'Dependency', 'dependency', 'deps')
                    return {
                        taskNo: getField(task, 'TaskNo', 'taskNo', 'task_no', 'Step', 'step', 'number') || (index + 1),
                        task: getField(task, 'Task', 'task', 'Description', 'description', 'title', 'Title', 'step', 'action', 'Action') || '',
                        commands: getField(task, 'Commands', 'commands', 'Command', 'command', 'cmd', 'instructions', 'Instructions', 'details', 'Details') || '',
                        duration: Number(getField(task, 'Duration', 'duration', 'time', 'Time', 'minutes', 'Minutes', 'estimatedTime')) || 0,
                        taskOffset: 0, startTime: null, finishTime: null,
                        dependencies: Array.isArray(deps) ? deps.join(',') : String(deps || ''),
                    }
                })
                onGenerated(builtDescription, tasks, {
                    technologies: selectedTechnologies,
                    generationContext: additionalContext,
                })
                onOpenChange(false)
                setSelectedTechnologies([])
                setAdditionalContext('')
                setStatusMessage('')
            } else {
                setError('AI did not return any runbook tasks. Please try again.')
            }
        } catch (err: unknown) {
            setError((err as Error)?.message || 'Failed to generate runbook')
        } finally {
            setGenerating(false)
            setStatusMessage('')
        }
    }, [selectedTechnologies, builtDescription, additionalContext, selectedScenario, onGenerated, onOpenChange])

    const handleClose = (nextOpen: boolean) => {
        if (!nextOpen && !generating) {
            setSelectedTechnologies([])
            setTechnologySearch('')
            setSelectedScenario(null)
            setAdditionalContext('')
            setError(null)
            setStatusMessage('')
        }
        onOpenChange(nextOpen)
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[800px]">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Sparkles className="h-5 w-5 text-primary" />
                        {t('masterRunbooks.aiGenerate', 'AI Generate Runbook')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('masterRunbooks.aiGenerateDescription', 'Build up the technology stack to generate a recovery runbook for.')}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {builtDescription && (
                        <div className="rounded-lg border bg-muted/50 p-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
                                {t('masterRunbooks.runbookFor', 'Runbook for')}
                            </p>
                            <p className="text-base font-medium">{fullDescription}</p>
                        </div>
                    )}

                    {/* Recovery Scenario selector */}
                    <div className="space-y-2">
                        <Label>{t('masterRunbooks.recoveryScenario', 'Recovery Scenario')}</Label>
                        <Select
                            value={selectedScenario?.id.toString() || ''}
                            onValueChange={(val) => {
                                if (!val || !scenariosData) {
                                    setSelectedScenario(null)
                                    return
                                }
                                const scenario = (scenariosData as any[]).find((s: any) => s.id.toString() === val)
                                if (scenario) {
                                    setSelectedScenario({ id: scenario.id, name: scenario.name, description: scenario.description || '' })
                                }
                            }}
                            disabled={generating || scenariosLoading}
                        >
                            <SelectTrigger className="w-full" disabled={generating}>
                                <SelectValue placeholder={t('masterRunbooks.selectScenario', 'Select a recovery scenario...')} />
                            </SelectTrigger>
                            <SelectContent>
                                {scenariosLoading ? (
                                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                                ) : scenariosData && (scenariosData as any[]).length > 0 ? (
                                    (scenariosData as any[]).map((scenario: any) => (
                                        <SelectItem key={scenario.id} value={scenario.id.toString()}>
                                            <span><TranslatableText text={scenario.name} /></span>
                                            {scenario.description && (
                                                <span className="ml-2 text-xs text-muted-foreground">— <TranslatableText text={scenario.description} /></span>
                                            )}
                                        </SelectItem>
                                    ))
                                ) : (
                                    <SelectItem value="none" disabled>No scenarios available</SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                        {selectedScenario?.description && (
                            <p className="text-xs text-muted-foreground"><TranslatableText text={selectedScenario.description} /></p>
                        )}
                    </div>

                    {/* Technology selector - same pattern as ObjectDetailsTab */}
                    <div className="space-y-2">
                        <Label>{t('masterRunbooks.addTechnology', 'Add Technology')}</Label>
                        <Select
                            value=""
                            onValueChange={handleAddTechnology}
                        >
                            <SelectTrigger className="w-full" disabled={generating}>
                                <SelectValue placeholder={t('masterRunbooks.chooseTechnology', 'Search and select a technology...')} />
                            </SelectTrigger>
                            <SelectContent>
                                <div className="px-2 py-1.5 border-b">
                                    <div className="relative">
                                        <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            placeholder="Search technologies..."
                                            value={technologySearch}
                                            onChange={(e) => setTechnologySearch(e.target.value)}
                                            className="pl-8 h-8"
                                            onClick={(e) => e.stopPropagation()}
                                            onKeyDown={(e) => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                                {technologiesLoading ? (
                                    <SelectItem value="loading" disabled>Loading...</SelectItem>
                                ) : filteredTechnologies.length > 0 ? (
                                    filteredTechnologies.map((tech: any) => {
                                        const isAlreadySelected = selectedTechnologies.some((s) => s.id === tech.id)
                                        return (
                                            <SelectItem
                                                key={tech.id}
                                                value={tech.id.toString()}
                                                disabled={isAlreadySelected}
                                                className={isAlreadySelected ? 'opacity-40' : ''}
                                            >
                                                {tech.name}
                                                {tech.category && (
                                                    <span className="ml-2 text-xs text-muted-foreground">({tech.category})</span>
                                                )}
                                            </SelectItem>
                                        )
                                    })
                                ) : (
                                    <SelectItem value="no-results" disabled>
                                        {technologySearch ? 'No technologies found' : 'No technologies available'}
                                    </SelectItem>
                                )}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Selected technologies - table layout */}
                    {selectedTechnologies.length > 0 && (
                        <div className="rounded-md border">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b bg-muted/50">
                                        <th className="text-left py-2 px-3 font-medium text-muted-foreground w-[80px]">#</th>
                                        <th className="text-left py-2 px-3 font-medium text-muted-foreground w-[100px]">Role</th>
                                        <th className="text-left py-2 px-3 font-medium text-muted-foreground w-[160px]">Category</th>
                                        <th className="text-left py-2 px-3 font-medium text-muted-foreground">Technology</th>
                                        <th className="w-[40px]"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedTechnologies.map((tech, index) => {
                                        const role = index === 0 ? 'Software' : index === 1 ? 'Hardware' : `Layer ${index + 1}`
                                        return (
                                            <tr key={tech.id} className="border-b last:border-b-0 hover:bg-muted/30">
                                                <td className="py-2 px-3 text-muted-foreground">{index + 1}</td>
                                                <td className="py-2 px-3">
                                                    <span className="inline-flex items-center rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                                                        {role}
                                                    </span>
                                                </td>
                                                <td className="py-2 px-3 text-muted-foreground">{tech.category}</td>
                                                <td className="py-2 px-3 font-medium">{tech.name}</td>
                                                <td className="py-2 px-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveTechnology(tech.id)}
                                                        className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
                                                        disabled={generating}
                                                    >
                                                        <X className="h-3.5 w-3.5" />
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Additional context */}
                    <div className="space-y-2">
                        <Label>{t('masterRunbooks.additionalContext', 'Additional Context (optional)')}</Label>
                        <Textarea
                            value={additionalContext}
                            onChange={(e) => setAdditionalContext(e.target.value)}
                            placeholder={t('masterRunbooks.additionalContextPlaceholder', 'e.g. Using iLO 5 for remote management, backup files on SMB share...')}
                            rows={3}
                            disabled={generating}
                        />
                    </div>

                    {generating && (
                        <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
                            <div className="flex items-center gap-3">
                                <DotSphereSpinner size={40} logoSrc="/images/vsl-logo.png" variant="light" />
                                <div>
                                    <p className="text-sm font-medium">
                                        {statusMessage ? cleanStatusMessage(statusMessage) : t('masterRunbooks.aiWorking', 'AI assistant is generating your runbook...')}
                                    </p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {t('masterRunbooks.aiGenerateWait', 'This may take a few minutes. Please do not navigate away from this page.')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                    {error && (
                        <div className="text-sm text-destructive bg-destructive/10 rounded-lg p-3">
                            {error}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={generating}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="button" onClick={handleGenerate} disabled={generating || selectedTechnologies.length === 0}>
                        {generating ? (
                            <>
                                <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                                {t('masterRunbooks.generating', 'Generating...')}
                            </>
                        ) : (
                            <>
                                <Sparkles className="h-4 w-4 mr-2" />
                                {t('masterRunbooks.generateRunbook', 'Generate Runbook')}
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
